/**
 * Lobby scene — 3-column game-show layout.
 *
 *   ┌─────────┬───────────────┬─────────┐
 *   │ TOP-10  │   WHO AM I?   │ STAND-  │
 *   │ leader  │   (title)     │ INGS    │
 *   │ board   │   subtitle    │ (tail   │
 *   ├─────────┤   how-to-play │  scroll)│
 *   │ HOW TO  │               │         │
 *   │  PLAY   │               │         │
 *   └─────────┴───────────────┴─────────┘
 *
 * Mirrors the WhatIs lobby layout. Uses the shared `.sg-lblb*` styles via
 * createLobbyLeaderboardTop / createLobbyLeaderboardTail and the HOW TO PLAY
 * panel from createLobbyHowTo. The center column carries the Bebas Neue
 * title with a soft pulsing question-mark glow and a one-line subtitle.
 */

import type { LbRow } from '@sg/shared';
import { createLobbyLeaderboardTop, createLobbyLeaderboardTail } from '../ui/lobby-leaderboard.js';
import { createLobbyHowTo } from '../ui/lobby-howto.js';

export interface LobbyHandle {
  el: HTMLDivElement;
  setLeaderboard(rows: LbRow[], allTime: LbRow[]): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes wai-lobby-glow {
    0%, 100% { text-shadow: 0 6px 0 var(--sg-secondary, #060CE9), 0 0 30px rgba(255,196,61,0.45); }
    50%      { text-shadow: 0 6px 0 var(--sg-secondary, #060CE9), 0 0 80px rgba(255,196,61,0.95); }
  }
  @keyframes wai-lobby-q-float {
    0%, 100% { transform: translateY(0) rotate(-4deg); opacity: 0.18; }
    50%      { transform: translateY(-22px) rotate(4deg); opacity: 0.30; }
  }
  .wai-lobby {
    position: absolute; inset: 0;
    display: grid;
    grid-template-columns: 1fr 1.4fr 1fr;
    gap: 28px;
    padding: 56px 64px;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Oswald', sans-serif;
  }
  .wai-lobby-col {
    display: flex; flex-direction: column;
    gap: 20px;
    min-height: 0;
  }
  .wai-lobby-center {
    position: relative;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center;
    overflow: hidden;
  }
  .wai-lobby-q-bg {
    position: absolute;
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 760px; line-height: 0.85;
    color: var(--sg-accent, #00F0FF);
    pointer-events: none;
    user-select: none;
    animation: wai-lobby-q-float 6s ease-in-out infinite;
    z-index: 0;
  }
  .wai-lobby-title-wrap { position: relative; z-index: 1; }
  .wai-lobby-eyebrow {
    font-family: 'Oswald', sans-serif;
    font-size: 22px; letter-spacing: 0.4em;
    color: var(--sg-accent, #00F0FF);
    text-transform: uppercase;
    opacity: 0.85;
    margin-bottom: 14px;
  }
  .wai-lobby-title {
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 220px; line-height: 0.9;
    color: var(--sg-primary, #FFC43D);
    letter-spacing: 6px;
  }
  .wai-lobby-title-big {
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-size: 320px; line-height: 0.9;
    color: var(--sg-primary, #FFC43D);
    letter-spacing: 12px;
    margin-top: 6px;
    animation: wai-lobby-glow 2.4s ease-in-out infinite;
  }
  .wai-lobby-sub {
    margin-top: 28px;
    font-size: 24px;
    color: #E2E8F0;
    opacity: 0.92;
    letter-spacing: 0.06em;
    max-width: 720px;
    line-height: 1.4;
  }
  .wai-lobby-cta {
    margin-top: 22px;
    display: inline-block;
    padding: 12px 28px;
    border: 2px solid var(--sg-primary, #FFC43D);
    border-radius: 999px;
    color: var(--sg-primary, #FFC43D);
    font-family: 'Oswald', sans-serif;
    font-size: 22px;
    letter-spacing: 0.18em;
    background: rgba(6,12,233,0.45);
  }
  .wai-lobby-cta code {
    color: var(--sg-primary, #FFC43D);
    font-family: ui-monospace, monospace;
    background: rgba(255,196,61,0.18);
    padding: 2px 8px;
    border-radius: 6px;
    margin: 0 4px;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('wai-lobby-style')) return;
  const s = document.createElement('style');
  s.id = 'wai-lobby-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createLobby(stage: HTMLElement): LobbyHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'wai-lobby';
  el.setAttribute('data-scene', 'lobby');
  el.setAttribute('data-testid', 'wai-lobby');

  // Left column: TOP-10 panel + HOW TO PLAY panel (matches WhatIs).
  const left = document.createElement('div');
  left.className = 'wai-lobby-col';
  const top10 = createLobbyLeaderboardTop(left);
  const howto = createLobbyHowTo(left);
  void howto;
  // Replace the default WhatIs how-to-play copy with Who Am I? specific steps.
  // (Game is letter-reveal: a hidden subject; one letter unlocks every 5 seconds
  //  for the duration of a 60s round. The fewer letters revealed when you guess,
  //  the more points you score. Earlier-rank guessers get a bigger slice.)
  const howtoBody = left.querySelector<HTMLElement>('.sg-howto-body ol');
  if (howtoBody) {
    howtoBody.innerHTML = `
      <li class="sg-howto-item">Streamer types <code>!start</code> to begin</li>
      <li class="sg-howto-item">Letters reveal one at a time — every 5 seconds</li>
      <li class="sg-howto-item">Type your guess in chat — fewer letters revealed = more points</li>
      <li class="sg-howto-item">First correct answer earns the most; later correct guesses still score</li>
      <li class="sg-howto-item">Top scorers crowned at the end of the game</li>
    `;
  }

  // Center column: branded title + subtitle + CTA.
  const center = document.createElement('div');
  center.className = 'wai-lobby-center';
  center.innerHTML = `
    <div class="wai-lobby-q-bg" aria-hidden="true">?</div>
    <div class="wai-lobby-title-wrap">
      <div class="wai-lobby-eyebrow">A MYSTERY GAME</div>
      <div class="wai-lobby-title">WHO</div>
      <div class="wai-lobby-title-big">AM I?</div>
      <div class="wai-lobby-sub">Letters reveal one at a time. One mystery subject.<br/>Type your guess in chat — guess early for max points.</div>
      <div class="wai-lobby-cta">Type <code>!start</code> to play</div>
    </div>
  `;

  // Right column: STANDINGS (ranks 11+) ping-pong scroll panel.
  const right = document.createElement('div');
  right.className = 'wai-lobby-col';
  const tail = createLobbyLeaderboardTail(right);

  el.appendChild(left);
  el.appendChild(center);
  el.appendChild(right);
  stage.appendChild(el);

  return {
    el,
    setLeaderboard(rows, allTime) {
      // Both panels share one ranked list. Prefer the longer "all-time" feed
      // when present (it includes ranks 11+ for the scrolling tail); fall back
      // to the snapshot top-10 so a fresh tenant still sees something.
      const source = (allTime && allTime.length > 0) ? allTime : rows;
      top10.setRows(source);
      tail.setRows(source);
    },
    setVisible(v) {
      el.style.display = v ? 'grid' : 'none';
    },
  };
}
